/** Event logs tab: RMM snapshot slug and display helpers. */

export const EVENT_LOG_SLUG = 'event-log-health'

export const EVENT_LOG_TAB_DESCRIPTION =
  'System and Application event log summary from the last 24 hours, with fault patterns over the last 7 days.'

export function eventLogSnapshotFromRmmData(data) {
  return (data?.snapshots ?? []).find((s) => s.script_slug === EVENT_LOG_SLUG) ?? null
}

export function levelBadgeVariant(level) {
  const text = String(level || '').toLowerCase()
  if (text === 'critical') return 'destructive'
  if (text === 'error') return 'destructive'
  if (text === 'warning') return 'warning'
  return 'secondary'
}

export function groupEventsByDate(events) {
  const rows = asEventArray(events)
  const groups = new Map()

  for (const event of rows) {
    if (!event?.occurred_at) continue
    const dateKey = event.occurred_at.slice(0, 10)
    if (!groups.has(dateKey)) groups.set(dateKey, [])
    groups.get(dateKey).push(event)
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, items]) => ({
      date,
      events: items.sort(
        (left, right) => new Date(right.occurred_at) - new Date(left.occurred_at),
      ),
    }))
}

export function filterEventsByLevel(events, levelFilter) {
  return filterEvents(events, { level: levelFilter })
}

const SERVICE_FAILURE_IDS = new Set([7000, 7009, 7011, 7023, 7031, 7034])
const DISK_PROVIDER_PATTERN = /disk|ntfs|stor|volmgr|partmgr|spaceport/i

export function filterEvents(events, filter = {}) {
  let rows = asEventArray(events)
  const level = filter.level
  const logName = filter.logName
  const pattern = filter.pattern
  const provider = filter.provider

  if (level && level !== 'all') {
    rows = rows.filter(
      (event) => String(event.level || '').toLowerCase() === level.toLowerCase(),
    )
  }

  if (logName) {
    rows = rows.filter(
      (event) => String(event.log_name || '').toLowerCase() === logName.toLowerCase(),
    )
  }

  if (provider) {
    rows = rows.filter((event) => String(event.provider || '') === provider)
  }

  if (pattern === 'shutdown') {
    rows = rows.filter(
      (event) =>
        Number(event.event_id) === 41 &&
        /kernel-power/i.test(String(event.provider || '')),
    )
  }

  if (pattern === 'disk') {
    rows = rows.filter((event) => {
      const levelText = String(event.level || '').toLowerCase()
      return (
        String(event.log_name || '').toLowerCase() === 'system' &&
        (levelText === 'critical' || levelText === 'error') &&
        DISK_PROVIDER_PATTERN.test(String(event.provider || ''))
      )
    })
  }

  if (pattern === 'service') {
    rows = rows.filter(
      (event) =>
        String(event.provider || '') === 'Service Control Manager' &&
        SERVICE_FAILURE_IDS.has(Number(event.event_id)),
    )
  }

  return rows
}

export function describeEventFilter(filter = {}) {
  const parts = []
  if (filter.logName) parts.push(`${filter.logName} log`)
  if (filter.level && filter.level !== 'all') parts.push(filter.level)
  if (filter.pattern === 'shutdown') parts.push('unexpected shutdowns (Kernel-Power 41)')
  if (filter.pattern === 'disk') parts.push('disk and storage errors')
  if (filter.pattern === 'service') parts.push('service control failures')
  if (filter.provider) parts.push(filter.provider)
  if (parts.length === 0) return 'All recent events'
  return parts.join(' · ')
}

export function eventFilterKey(filter = {}) {
  return JSON.stringify({
    level: filter.level ?? 'all',
    logName: filter.logName ?? null,
    pattern: filter.pattern ?? null,
    provider: filter.provider ?? null,
  })
}

export function isSameEventFilter(left = {}, right = {}) {
  return eventFilterKey(left) === eventFilterKey(right)
}

/** PowerShell ConvertTo-Json often emits a single object instead of a one-item array. */
export function asEventArray(value) {
  if (value == null) return []
  if (Array.isArray(value)) return value
  if (typeof value === 'object') return [value]
  return []
}

function collectAllSampleEvents(payload) {
  const samples = payload?.event_samples
  if (!samples) return asEventArray(payload?.recent_events)

  const rows = [
    ...asEventArray(samples.system?.critical),
    ...asEventArray(samples.system?.error),
    ...asEventArray(samples.system?.warning),
    ...asEventArray(samples.application?.critical),
    ...asEventArray(samples.application?.error),
    ...asEventArray(samples.application?.warning),
    ...asEventArray(samples.patterns?.shutdown),
    ...asEventArray(samples.patterns?.disk),
    ...asEventArray(samples.patterns?.service),
  ]

  const seen = new Set()
  return rows.filter((event) => {
    const key = `${event.occurred_at}|${event.log_name}|${event.event_id}|${event.provider}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function selectEventsForFilter(payload, filter = {}) {
  const samples = payload?.event_samples
  if (!samples) {
    return filterEvents(asEventArray(payload?.recent_events), filter)
  }

  let rows = []

  if (filter.pattern === 'shutdown') rows = asEventArray(samples.patterns?.shutdown)
  else if (filter.pattern === 'disk') rows = asEventArray(samples.patterns?.disk)
  else if (filter.pattern === 'service') rows = asEventArray(samples.patterns?.service)
  else {
    const log = filter.logName?.toLowerCase()
    const level = filter.level?.toLowerCase()

    if (log === 'system' && level && level !== 'all') {
      rows = asEventArray(samples.system?.[level])
    } else if (log === 'application' && level && level !== 'all') {
      rows = asEventArray(samples.application?.[level])
    } else if (filter.provider) {
      rows = collectAllSampleEvents(payload).filter(
        (event) => String(event.provider || '') === filter.provider,
      )
    } else if (level && level !== 'all') {
      rows = collectAllSampleEvents(payload).filter(
        (event) => String(event.level || '').toLowerCase() === level,
      )
    } else {
      rows = payload.recent_events?.length
        ? asEventArray(payload.recent_events)
        : collectAllSampleEvents(payload)
    }
  }

  if (rows.length === 0 && (getSummaryCountForFilter(payload, filter) ?? 0) > 0) {
    const fallbackPool = [
      ...collectAllSampleEvents(payload),
      ...asEventArray(payload?.recent_events),
    ]
    rows = filterEvents(fallbackPool, filter)
  }

  return rows
}

export function getSummaryCountForFilter(payload, filter = {}) {
  if (!payload) return null

  if (filter.pattern === 'shutdown') return payload.patterns?.unexpected_shutdowns ?? null
  if (filter.pattern === 'disk') return payload.patterns?.disk_storage_errors ?? null
  if (filter.pattern === 'service') return payload.patterns?.service_control_failures ?? null

  const log = filter.logName?.toLowerCase()
  const level = filter.level?.toLowerCase()
  const summary = log === 'application' ? payload.application : log === 'system' ? payload.system : null

  if (summary && level && level !== 'all') {
    return summary[level] ?? null
  }

  return null
}

export function countEventSamples(payload) {
  if (!payload?.event_samples) return 0
  return collectAllSampleEvents(payload).length
}

export function snapshotNeedsResample(payload) {
  if (!payload) return false
  if (!payload.event_samples) return true

  const checks = [
    ['system', 'critical', payload.system?.critical],
    ['system', 'error', payload.system?.error],
    ['system', 'warning', payload.system?.warning],
    ['application', 'critical', payload.application?.critical],
    ['application', 'error', payload.application?.error],
    ['application', 'warning', payload.application?.warning],
  ]

  for (const [log, level, count] of checks) {
    if ((count ?? 0) <= 0) continue
    const samples = asEventArray(payload.event_samples?.[log]?.[level])
    const sampleCount = payload.sample_counts?.[log]?.[level]
    const hasSamples = samples.length > 0 || (sampleCount ?? 0) > 0
    if (!hasSamples) return true
  }

  return false
}
